import React from 'react';

import { useAuthentication } from '@context/AuthContext';
import { listenToFollowingUserIds } from '@db/Follows';
import { useUsersQuery } from '@db/Users/Queries';
import { createNamedContext, useRequiredContext } from '@helpers/Context';
import { logError } from '@helpers/Loggers';
import { User } from '@types';

const EMPTY_FOLLOWING_USER_IDS = new Set<string>();

export type FollowingContextProps = {
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
  followingUserIdsLoading: boolean;
  followingUserIdsError: boolean;
  followingUserIds: Set<string>;
  followingUsers: User[];
};

export const FollowingContext = createNamedContext<FollowingContextProps>({
  FollowingContext: undefined,
});

export function useFollowing() {
  return useRequiredContext(FollowingContext);
}

export function FollowingProvider({ children }: React.PropsWithChildren) {
  const { loggedIn } = useAuthentication();

  const [error, setError] = React.useState(false);
  const [followingUserIds, setFollowingUserIds] = React.useState<Set<string>>(new Set());
  const [followingIdsLoading, setFollowingIdsLoading] = React.useState(true);
  const followingUserIdList = React.useMemo(
    () => [...followingUserIds.values()],
    [followingUserIds]
  );
  const followingUsersQuery = useUsersQuery({
    enabled: loggedIn && !followingIdsLoading,
    userIds: followingUserIdList,
  });

  React.useEffect(() => {
    if (!loggedIn) {
      return;
    }

    // Listener lifecycle changes reset the subscription state before the first snapshot arrives.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setError(false);
    setFollowingIdsLoading(true);
    return listenToFollowingUserIds(
      (userIds) => {
        setError(false);
        setFollowingUserIds(userIds);
        setFollowingIdsLoading(false);
      },
      (error) => {
        setError(true);
        setFollowingIdsLoading(false);
        logError(error);
      }
    );
  }, [loggedIn]);

  const followingUserIdsLoading = loggedIn && followingIdsLoading;
  const followingUserIdsError = loggedIn && error;
  const resolvedFollowingUserIds = loggedIn ? followingUserIds : EMPTY_FOLLOWING_USER_IDS;
  const value = {
    loading: followingUserIdsLoading || followingUsersQuery.loading,
    error: followingUserIdsError || followingUsersQuery.error,
    reload: followingUsersQuery.reload,
    followingUserIdsLoading,
    followingUserIdsError,
    followingUserIds: resolvedFollowingUserIds,
    followingUsers: loggedIn ? followingUsersQuery.users : [],
  };

  return <FollowingContext.Provider value={value}>{children}</FollowingContext.Provider>;
}
